import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    auth, provider, RecaptchaVerifier, PhoneAuthProvider,
    PhoneMultiFactorGenerator, multiFactor, getMultiFactorResolver, signInWithPopup
} from './firebaseConfig';
import {
    Button, Typography, Box, TextField, Paper, CircularProgress,
    Alert, Stepper, Step, StepLabel, Fade, Card, Divider
} from '@mui/material';
import { Google, PhoneAndroid, VerifiedUser, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const MFA_STEPS = ['Sign in with Google', 'Verify Phone Number', 'Access Granted'];

function ensureRecaptchaContainer() {
    let el = document.getElementById('ohana-recaptcha-widget');
    if (!el) {
        el = document.createElement('div');
        el.id = 'ohana-recaptcha-widget';
        document.body.appendChild(el);
    } else if (!document.body.contains(el)) {
        document.body.appendChild(el);
    }
    el.style.cssText = 'position:fixed;bottom:-100px;left:-100px;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;';
    return el;
}

/**
 * Fully tear down reCAPTCHA. Do not strip the container with removeChild — Google's script
 * keeps async references and will throw "Cannot read properties of null (reading 'style')".
 */
function destroyRecaptchaWidget() {
    if (window._ohanaRecaptcha) {
        try {
            window._ohanaRecaptcha.clear();
        } catch (_) { /* already cleared or DOM gone */ }
        window._ohanaRecaptcha = null;
    }
    const el = document.getElementById('ohana-recaptcha-widget');
    if (el?.parentNode) {
        el.parentNode.removeChild(el);
    }
}

function getOrCreateRecaptcha() {
    const existingEl = document.getElementById('ohana-recaptcha-widget');
    if (window._ohanaRecaptcha && existingEl && document.body.contains(existingEl)) {
        return window._ohanaRecaptcha;
    }
    destroyRecaptchaWidget();

    const el = ensureRecaptchaContainer();
    window._ohanaRecaptcha = new RecaptchaVerifier(auth, el, {
        size: 'invisible',
        callback: () => {},
        'expired-callback': () => {
            destroyRecaptchaWidget();
        },
    });
    return window._ohanaRecaptcha;
}

function resetRecaptcha() {
    destroyRecaptchaWidget();
}

function Login({ onMfaComplete }) {
    const navigate = useNavigate();
    const attemptedRef = useRef(false);

    useEffect(() => () => destroyRecaptchaWidget(), []);

    const [step, setStep] = useState('idle');
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');

    const [mfaResolver, setMfaResolver] = useState(null);
    const [verificationId, setVerificationId] = useState('');
    const [verificationCode, setVerificationCode] = useState('');

    const [phoneNumber, setPhoneNumber] = useState('');
    const [enrollVerificationId, setEnrollVerificationId] = useState('');
    const [enrollCode, setEnrollCode] = useState('');
    const [pendingUser, setPendingUser] = useState(null);

    const completeLogin = useCallback(() => {
        sessionStorage.setItem('mfaVerified', 'true');
        if (onMfaComplete) onMfaComplete();
        setActiveStep(2);
        setStep('done');
    }, [onMfaComplete]);

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError('');
        setInfo('');
        attemptedRef.current = true;

        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            const enrolled = multiFactor(user).enrolledFactors;

            if (enrolled.length === 0) {
                setPendingUser(user);
                setStep('enroll-phone');
                setActiveStep(1);
                setInfo('Welcome! For security, please set up phone verification.');
            } else {
                completeLogin();
                navigate('/');
            }
        } catch (err) {
            if (err.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(auth, err);
                setMfaResolver(resolver);

                const phoneHint = resolver.hints.find(
                    h => h.factorId === PhoneAuthProvider.PHONE_FACTOR_ID
                ) || resolver.hints[0];

                if (!phoneHint) {
                    setError('No MFA factors found. Please contact support.');
                    setLoading(false);
                    return;
                }

                const displayPhone = phoneHint.phoneNumber || phoneHint.displayName || 'your device';
                setInfo(`Verification code sent to ${displayPhone}`);
                setStep('mfa-challenge');
                setActiveStep(1);

                try {
                    const phoneInfoOptions = { multiFactorHint: phoneHint, session: resolver.session };
                    const phoneProvider = new PhoneAuthProvider(auth);
                    const verifier = getOrCreateRecaptcha();
                    const vId = await phoneProvider.verifyPhoneNumber(phoneInfoOptions, verifier);
                    setVerificationId(vId);
                } catch (smsErr) {
                    resetRecaptcha();
                    setError('Failed to send SMS: ' + smsErr.message);
                }
            } else {
                setError(err.message || 'Sign-in failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleMfaChallenge = async () => {
        if (!verificationCode || verificationCode.length < 6) {
            setError('Please enter the 6-digit code');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
            const assertion = PhoneMultiFactorGenerator.assertion(cred);
            await mfaResolver.resolveSignIn(assertion);
            completeLogin();
            navigate('/');
        } catch (err) {
            setError('Invalid code. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSendEnrollCode = async () => {
        const cleaned = phoneNumber.replace(/[\s\-()]/g, '');
        const formatted = cleaned.startsWith('+') ? cleaned : '+1' + cleaned;
        if (formatted.length < 11) {
            setError('Enter a valid phone number with country code (e.g. +14155551234)');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const session = await multiFactor(pendingUser).getSession();
            const phoneInfoOptions = { phoneNumber: formatted, session };
            const phoneProvider = new PhoneAuthProvider(auth);
            const verifier = getOrCreateRecaptcha();
            const vId = await phoneProvider.verifyPhoneNumber(phoneInfoOptions, verifier);
            setEnrollVerificationId(vId);
            setStep('enroll-verify');
            setInfo(`Code sent to ${phoneNumber}`);
        } catch (err) {
            resetRecaptcha();
            setError('Failed to send code: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEnrollVerify = async () => {
        if (!enrollCode || enrollCode.length < 6) {
            setError('Please enter the 6-digit code');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const cred = PhoneAuthProvider.credential(enrollVerificationId, enrollCode);
            const assertion = PhoneMultiFactorGenerator.assertion(cred);
            await multiFactor(pendingUser).enroll(assertion, 'Phone Number');
            completeLogin();
            setInfo('MFA enrolled successfully!');
            setTimeout(() => navigate('/'), 1500);
        } catch (err) {
            setError('Verification failed: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            p: 2
        }}>
            <Fade in timeout={800}>
                <Paper elevation={12} sx={{
                    maxWidth: 480, width: '100%', borderRadius: 4, overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                }}>
                    <Box sx={{
                        background: 'linear-gradient(135deg, #1a237e, #4a148c)',
                        p: 4, textAlign: 'center', color: 'white'
                    }}>
                        <Lock sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Ohana Financial Services
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                            Secure login with multi-factor authentication
                        </Typography>
                    </Box>

                    <Box sx={{ p: 4 }}>
                        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                            {MFA_STEPS.map(label => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        {info && !error && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

                        {step === 'idle' && (
                            <Box sx={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained" fullWidth size="large"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Google />}
                                    onClick={handleGoogleLogin}
                                    disabled={loading}
                                    sx={{
                                        py: 1.5, borderRadius: 3, fontWeight: 600, fontSize: '1rem',
                                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                        textTransform: 'none',
                                        '&:hover': { background: 'linear-gradient(135deg, #5a6fd6, #6a3f94)' }
                                    }}
                                >
                                    Sign in with Google
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                    You'll be asked to verify your phone number for added security
                                </Typography>
                            </Box>
                        )}

                        {step === 'mfa-challenge' && (
                            <Card variant="outlined" sx={{ borderRadius: 3, p: 3, textAlign: 'center' }}>
                                <PhoneAndroid sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Verify Your Identity
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Enter the 6-digit code sent to your phone
                                </Typography>
                                <TextField
                                    fullWidth autoFocus label="Verification Code" value={verificationCode}
                                    onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: 8 } }}
                                    sx={{ mb: 3 }}
                                    onKeyDown={e => e.key === 'Enter' && handleMfaChallenge()}
                                />
                                <Button
                                    variant="contained" fullWidth size="large"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <VerifiedUser />}
                                    onClick={handleMfaChallenge}
                                    disabled={loading || verificationCode.length < 6}
                                    sx={{
                                        py: 1.5, borderRadius: 3, fontWeight: 600,
                                        background: 'linear-gradient(135deg, #43e97b, #38f9d7)',
                                        color: '#1a237e', textTransform: 'none'
                                    }}
                                >
                                    Verify & Sign In
                                </Button>
                            </Card>
                        )}

                        {step === 'enroll-phone' && (
                            <Card variant="outlined" sx={{ borderRadius: 3, p: 3, textAlign: 'center' }}>
                                <PhoneAndroid sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Set Up Phone Verification
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Add your phone number for two-factor authentication
                                </Typography>
                                <TextField
                                    fullWidth autoFocus label="Phone Number" value={phoneNumber}
                                    onChange={e => setPhoneNumber(e.target.value)}
                                    placeholder="4155551234 or +14155551234"
                                    sx={{ mb: 3 }}
                                    onKeyDown={e => e.key === 'Enter' && handleSendEnrollCode()}
                                />
                                <Button
                                    variant="contained" fullWidth size="large"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PhoneAndroid />}
                                    onClick={handleSendEnrollCode}
                                    disabled={loading || phoneNumber.length < 10}
                                    sx={{
                                        py: 1.5, borderRadius: 3, fontWeight: 600,
                                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                        textTransform: 'none'
                                    }}
                                >
                                    Send Verification Code
                                </Button>
                                <Divider sx={{ my: 2 }} />
                                <Button
                                    fullWidth size="small" color="inherit"
                                    onClick={() => { completeLogin(); navigate('/'); }}
                                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                                >
                                    Skip for now
                                </Button>
                            </Card>
                        )}

                        {step === 'enroll-verify' && (
                            <Card variant="outlined" sx={{ borderRadius: 3, p: 3, textAlign: 'center' }}>
                                <VerifiedUser sx={{ fontSize: 40, color: '#43e97b', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Enter Verification Code
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    We sent a code to {phoneNumber}
                                </Typography>
                                <TextField
                                    fullWidth autoFocus label="6-digit Code" value={enrollCode}
                                    onChange={e => setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: 8 } }}
                                    sx={{ mb: 3 }}
                                    onKeyDown={e => e.key === 'Enter' && handleEnrollVerify()}
                                />
                                <Button
                                    variant="contained" fullWidth size="large"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <VerifiedUser />}
                                    onClick={handleEnrollVerify}
                                    disabled={loading || enrollCode.length < 6}
                                    sx={{
                                        py: 1.5, borderRadius: 3, fontWeight: 600,
                                        background: 'linear-gradient(135deg, #43e97b, #38f9d7)',
                                        color: '#1a237e', textTransform: 'none'
                                    }}
                                >
                                    Complete Setup
                                </Button>
                            </Card>
                        )}

                        {step === 'done' && (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <VerifiedUser sx={{ fontSize: 56, color: '#43e97b', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                    Verified Successfully
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Redirecting to dashboard...
                                </Typography>
                                <CircularProgress size={24} sx={{ mt: 2 }} />
                            </Box>
                        )}
                    </Box>

                    <Divider />
                    <Box sx={{ p: 2, textAlign: 'center', backgroundColor: '#fafafa' }}>
                        <Typography variant="caption" color="text.secondary">
                            Protected by Firebase Multi-Factor Authentication
                        </Typography>
                    </Box>
                </Paper>
            </Fade>
        </Box>
    );
}

export default Login;
